import { useId, useState } from 'react';
import { Badge, Button, CompanyLogo, FilterChips, Input, LinkButton, SectionCard, Select, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDate } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { Opportunity, SegmentFamily, SegmentKey } from '../types';
import { SEGMENT_FAMILY_LABEL, SEGMENT_LABEL, normalizarBusca } from '../vocabulario';

/**
 * Radar de oportunidades: publicos PARADOS de todas as empresas, com o
 * retorno esperado pelo historico de cada uma e um atalho para preparar o
 * envio.
 *
 * Decisoes nao obvias:
 *  1. So publico pos-fato (abandonou, recusado, venceu, respondeu sem
 *     fechar). E a lista do que ja e dinheiro na mesa: nenhum item aqui e
 *     "falar com quem ainda nao saiu".
 *  2. Ordena por retorno esperado; sem historico, pelo valor parado -- e a
 *     linha diz "sem histórico" em vez de inventar taxa.
 *  3. "Preparar envio" troca a empresa em foco e abre o formulario ja com o
 *     publico marcado. Empresa sem numero conectado troca o botao por
 *     "Conectar número": disparar sem canal e impossivel, e a tela diz o
 *     caminho.
 *  4. Escala: busca por empresa, familia com contagem, ordenacao e "ver
 *     mais" em lotes de 8.
 */

type Ordem = 'retorno' | 'parado' | 'contatos' | 'sem_envio';
type FiltroFamilia = 'todas' | SegmentFamily;

const LOTE = 8;

const ORDENS: readonly { readonly value: Ordem; readonly label: string }[] = [
  { value: 'retorno', label: 'Maior retorno esperado' },
  { value: 'parado', label: 'Mais dinheiro parado' },
  { value: 'contatos', label: 'Mais contatos' },
  { value: 'sem_envio', label: 'Há mais tempo sem envio' },
];

function isOrdem(v: string): v is Ordem {
  return ORDENS.some((o) => o.value === v);
}

function comparar(a: Opportunity, b: Opportunity, ordem: Ordem): number {
  switch (ordem) {
    case 'retorno':
      return (b.expectedRecoveredCents ?? -1) - (a.expectedRecoveredCents ?? -1) || b.stalledAmountCents - a.stalledAmountCents;
    case 'parado':
      return b.stalledAmountCents - a.stalledAmountCents;
    case 'contatos':
      return b.contacts - a.contacts;
    case 'sem_envio': {
      const va = a.lastBroadcastAt ?? '';
      const vb = b.lastBroadcastAt ?? '';
      return va < vb ? -1 : va > vb ? 1 : b.stalledAmountCents - a.stalledAmountCents;
    }
  }
}

export function RadarDeOportunidades({
  oportunidades,
  companies,
  onPreparar,
}: {
  readonly oportunidades: readonly Opportunity[];
  readonly companies: readonly CompanySummary[];
  readonly onPreparar: (companyId: string, segmento: SegmentKey) => void;
}): JSX.Element {
  const buscaId = useId();
  const [busca, setBusca] = useState('');
  const [familia, setFamilia] = useState<FiltroFamilia>('todas');
  const [ordem, setOrdem] = useState<Ordem>('retorno');
  const [limite, setLimite] = useState(LOTE);

  const empresaDe = (id: string): CompanySummary | null => companies.find((c) => c.id === id) ?? null;
  const termo = normalizarBusca(busca.trim());
  const porBusca = oportunidades.filter((o) => termo === '' || normalizarBusca(`${empresaDe(o.companyId)?.name ?? ''} ${SEGMENT_LABEL[o.segmentKey]}`).includes(termo));
  const filtradas = porBusca.filter((o) => familia === 'todas' || o.family === familia);
  const ordenadas = [...filtradas].sort((a, b) => comparar(a, b, ordem));
  const visiveis = ordenadas.slice(0, limite);
  const totalParado = filtradas.reduce((acc, o) => acc + o.stalledAmountCents, 0);
  const totalEsperado = filtradas.reduce((acc, o) => acc + (o.expectedRecoveredCents ?? 0), 0);

  const contar = (f: SegmentFamily): string => formatCount(porBusca.filter((o) => o.family === f).length);
  const opcoes: readonly ChipOption<FiltroFamilia>[] = [
    { value: 'todas', label: 'Todas', count: formatCount(porBusca.length) },
    { value: 'carrinho', label: SEGMENT_FAMILY_LABEL.carrinho, count: contar('carrinho') },
    { value: 'comercial', label: SEGMENT_FAMILY_LABEL.comercial, count: contar('comercial') },
    { value: 'expirados', label: SEGMENT_FAMILY_LABEL.expirados, count: contar('expirados') },
  ];

  return (
    <SectionCard
      id="radar"
      icon="raio"
      title="Radar de oportunidades"
      description="Públicos que já passaram pelo fato e estão parados, em todas as empresas, com o retorno esperado pelo histórico de cada uma."
      aside={
        <Badge tone="brand" icon="moeda">
          {formatBRLFromCents(totalParado)} parados
        </Badge>
      }
      padded={false}
    >
      <div className="flex flex-col gap-3 px-5 pb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex w-full flex-col gap-1 sm:w-64">
            <label htmlFor={buscaId} className="text-xs font-medium text-ink-subtle">
              Buscar empresa ou público
            </label>
            <Input
              id={buscaId}
              size="sm"
              type="search"
              autoComplete="off"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setLimite(LOTE);
              }}
            />
          </div>
          <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-subtle sm:w-60">
            Ordenar por
            <Select
              size="sm"
              value={ordem}
              onChange={(e) => {
                if (isOrdem(e.target.value)) {
                  setOrdem(e.target.value);
                  setLimite(LOTE);
                }
              }}
            >
              {ORDENS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </label>
          <p className="ml-auto text-xs text-ink-subtle">
            Retorno esperado somado: <span className="font-semibold text-ink">{formatBRLFromCents(totalEsperado)}</span> (só onde há histórico)
          </p>
        </div>
        <FilterChips
          options={opcoes}
          value={familia}
          onChange={(v) => {
            setFamilia(v);
            setLimite(LOTE);
          }}
          label="Filtrar por família do público"
          prefix="Família:"
        />
      </div>

      {visiveis.length === 0 ? (
        <p className="border-t border-line px-5 py-6 text-sm text-ink-muted">
          {oportunidades.length === 0 ? 'Nenhum público parado: todo mundo que abandonou ou venceu já voltou ou está na régua automática.' : 'Nenhum público com esses filtros.'}
        </p>
      ) : (
        <ul className="divide-y divide-line/70 border-t border-line">
          {visiveis.map((o) => {
            const empresa = empresaDe(o.companyId);
            return (
              <li key={`${o.companyId}:${o.segmentKey}`} className="flex flex-col gap-3 px-5 py-3 transition-colors hover:bg-ink/[0.03] md:flex-row md:items-center md:gap-5">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {empresa === null ? null : <CompanyLogo company={empresa} size="md" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{empresa?.name ?? 'Empresa'}</p>
                    <p className="text-xs text-ink-muted">
                      {SEGMENT_LABEL[o.segmentKey]} · {formatCount(o.contacts)} contatos
                    </p>
                  </div>
                </div>
                <dl className="grid shrink-0 grid-cols-3 gap-3 text-xs md:w-[26rem]">
                  <div>
                    <dt className="text-ink-subtle">Parado</dt>
                    <dd className="font-medium tabular-nums text-ink">{formatBRLFromCents(o.stalledAmountCents)}</dd>
                  </div>
                  <div>
                    <dt className="text-ink-subtle">Retorno esperado</dt>
                    <dd className="font-medium tabular-nums text-ink">{o.expectedRecoveredCents === null ? 'sem histórico' : formatBRLFromCents(o.expectedRecoveredCents)}</dd>
                  </div>
                  <div>
                    <dt className="text-ink-subtle">Último envio</dt>
                    <dd className="tabular-nums text-ink">{o.lastBroadcastAt === null ? 'nenhum no período' : formatDate(o.lastBroadcastAt)}</dd>
                  </div>
                </dl>
                <div className="shrink-0">
                  {o.senderReady ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="disparo"
                      onClick={() => onPreparar(o.companyId, o.segmentKey)}
                      aria-label={`Preparar envio de ${empresa?.name ?? 'empresa'} para ${SEGMENT_LABEL[o.segmentKey]}`}
                    >
                      Preparar envio
                    </Button>
                  ) : (
                    <LinkButton to={`/admin/empresas/${o.companyId}`} variant="ghost" size="sm" icon="whatsapp">
                      Conectar número
                    </LinkButton>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-xs text-ink-subtle">
        <span>
          Mostrando {formatCount(visiveis.length)} de {formatCount(ordenadas.length)}
        </span>
        {ordenadas.length > visiveis.length ? (
          <Button variant="secondary" size="sm" icon="chevron" onClick={() => setLimite((l) => l + LOTE)}>
            Ver mais ({formatCount(ordenadas.length - visiveis.length)} restantes)
          </Button>
        ) : null}
      </div>
    </SectionCard>
  );
}
